import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchPublicEmploymentJobs } from '../services/EmploymentJobService';
import './FlexibleEmployment.scss';

function normalizeSkills(job) {
  if (Array.isArray(job?.skillsRequired)) return job.skillsRequired.filter(Boolean);
  if (typeof job?.skillsRequired === 'string') {
    return job.skillsRequired.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function formatDate(dateValue) {
  if (!dateValue) return 'Not set';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString();
}

export default function PublicEmploymentJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const runSearch = (criteria) => {
    setLoading(true);
    setError('');

    searchPublicEmploymentJobs(criteria)
      .then((data) => setJobs(Array.isArray(data) ? data : []))
      .catch((fetchError) => {
        console.error('Failed to load public employment jobs:', fetchError);
        setError('Unable to load open jobs right now.');
        setJobs([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    runSearch(null);
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmedJobTitle = jobTitle.trim();
    runSearch(trimmedJobTitle ? { jobTitle: trimmedJobTitle, location: '', cityName: '' } : null);
  };

  const openJobs = useMemo(() => jobs.filter((job) => (job?.jobStatus || 'open').toLowerCase() !== 'closed'), [jobs]);
  const highlightedLocations = useMemo(() => {
    const locations = openJobs
      .map((job) => job?.jobLocation || job?.cityName || '')
      .filter(Boolean);

    return [...new Set(locations)].slice(0, 3);
  }, [openJobs]);

  return (
    <div className="flex-employment-page flex-employment-public-page">
      <div className="flex-employment-shell">
        <div className="flex-employment-hero flex-employment-public-hero">
          <div className="flex-employment-public-hero-copy">
            <span className="flex-employment-kicker">Public jobs board</span>
            <h1>Find standout casual and flexible work around you.</h1>
            <p>Browse live short-term roles from cafes, events, venues, and local businesses. Search quickly, review the details, and apply when the fit feels right.</p>
            <div className="flex-employment-public-pills">
              <span>{openJobs.length} open roles</span>
              <span>No sign-in to browse</span>
              <span>Fast apply after registration</span>
            </div>
          </div>
          <div className="flex-employment-hero-panel">
            <div className="flex-employment-hero-stat">
              <strong>{openJobs.length}</strong>
              <span>Open opportunities</span>
            </div>
            <div className="flex-employment-hero-stat">
              <strong>{highlightedLocations.length || 1}</strong>
              <span>Active locations</span>
            </div>
            <div className="flex-employment-hero-locations">
              {highlightedLocations.length > 0 ? highlightedLocations.map((location) => (
                <span key={location}>{location}</span>
              )) : <span>New roles updating daily</span>}
            </div>
          </div>
        </div>

        <form className="flex-employment-search" onSubmit={handleSearch}>
          <div className="flex-employment-field flex-employment-search-field">
            <label htmlFor="employment-job-search-title">Search by job title</label>
            <input
              id="employment-job-search-title"
              type="text"
              value={jobTitle}
              onChange={(event) => setJobTitle(event.target.value)}
              placeholder="Barista, Ticketing officer, Cafe staff"
            />
          </div>
          <div className="flex-employment-search-actions">
            <button className="flex-employment-button" type="submit" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>

        <div className="flex-employment-public-toolbar">
          <div>
            <span className="flex-employment-kicker">Live results</span>
            <h2>{openJobs.length} roles ready to explore</h2>
          </div>
          <p>Every listing below links to a dedicated job page where candidates can review the full brief before applying.</p>
        </div>

        {error && <div className="flex-employment-error">{error}</div>}
        {!error && <div className="flex-employment-banner">Open jobs are available publicly. Applying will take you through registration and flexible-worker onboarding.</div>}

        {loading && <div className="flex-employment-loading">Loading jobs...</div>}
        {!loading && openJobs.length === 0 && <div className="flex-employment-empty">No open jobs are available right now.</div>}

        {!loading && openJobs.length > 0 && (
          <div className="flex-employment-grid">
            {openJobs.map((job) => {
              const jobId = job?.id || job?.jobId || '';
              const skills = normalizeSkills(job);

              return (
                <div className="flex-employment-card flex-employment-public-card" key={jobId || job.jobTitle}>
                  <div className="flex-employment-public-card-top">
                    <span className="flex-employment-status open">open</span>
                    <span className="flex-employment-public-rate">{job.hourlyRate ? `$${job.hourlyRate}/hr` : 'Rate on request'}</span>
                  </div>
                  <div>
                    <h3>{job.jobTitle || 'Untitled temporary role'}</h3>
                    <p>{job.jobDescription || 'No description provided.'}</p>
                  </div>

                  <div className="flex-employment-meta">
                    <div>
                      <strong>Dates</strong>
                      {job.fromDate && job.toDate && (
                        <span>{formatDate(job.fromDate)} to {formatDate(job.toDate)}</span>
                      )}
                    </div>
                    <div>
                      <strong>Closing date</strong>
                      <span>{formatDate(job.adClosingDate)}</span>
                    </div>
                    <div>
                      <strong>Location</strong>
                      <span>{job.jobLocation || job.cityName || 'Not set'}</span>
                    </div>
                    <div>
                      <strong>No of people required</strong>
                      <span>{job.numberOfPeopleRequired || 1}</span>
                    </div>
                    <div>
                      <strong>Type</strong>
                      <span>{job.employmentType || 'Not set'}</span>
                    </div>
                  </div>

                  <div className="flex-employment-skills">
                    {skills.length > 0 ? skills.map((skill) => (
                      <span className="flex-employment-skill" key={skill}>{skill}</span>
                    )) : <span className="flex-employment-public-muted">No skills listed</span>}
                  </div>

                  <div className="flex-employment-card-actions">
                    <button className="flex-employment-button" type="button" onClick={() => navigate(`/employment-jobs/${jobId}`)} disabled={!jobId}>
                      View job
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}